import type { Database } from "@/lib/supabase-types";

export type AppRole = Database["public"]["Enums"]["app_role"];

export type StaffRole = "super_admin" | "tech" | "admin";

/** Maps stored roles to role-tier cards (directory overview). */
export function displayStaffTier(
  dbRoles: AppRole[]
): "super_admin" | "tech" | "admin" | "operator" | "owner" | "tenant" {
  if (dbRoles.includes("super_admin")) return "super_admin";
  if (dbRoles.includes("tech")) return "tech";
  if (dbRoles.includes("admin")) return "admin";
  if (dbRoles.includes("operator")) return "operator";
  if (dbRoles.includes("owner")) return "owner";
  return "tenant";
}

export function parseUserRoles(
  rows: { role: AppRole }[] | null | undefined
): AppRole[] {
  if (!rows?.length) return [];
  return rows.map((r) => r.role);
}

export function hasStaffRole(roles: AppRole[]): boolean {
  return roles.some(
    (r) => r === "super_admin" || r === "tech" || r === "admin"
  );
}

export function isOwnerOnly(roles: AppRole[]): boolean {
  return roles.includes("owner") && !hasStaffRole(roles);
}

export function hasOperatorRole(roles: AppRole[]): boolean {
  return roles.includes("operator");
}

/**
 * Operator without CoLiviq staff roles — sees the operator-scoped console
 * (OPERATOR_NAV, data limited by operator RLS). CoLiviq staff who also hold
 * the operator role for testing keep the full staff console.
 */
export function isOperatorPortal(roles: AppRole[]): boolean {
  return hasOperatorRole(roles) && !hasStaffRole(roles);
}

/**
 * May edit tenant records, their date of birth, handover protocols and booking
 * status. Client-side mirror of the SQL `has_role(uid, 'admin')` predicate,
 * which is true for `admin` and `super_admin` and false for `tech` — keep the
 * two in step, since the `bookings` status trigger enforces the SQL side and a
 * drift here would surface as a raw DB error instead of a hidden control.
 */
export function canManageTenant(roles: AppRole[]): boolean {
  return roles.includes("admin") || roles.includes("super_admin");
}

/**
 * May "act as" (impersonate) a tenant or owner account. Client-side mirror of
 * the `admin-act-as-tenant` edge-function gate, which accepts `admin` and
 * `super_admin` — keep the two in step, or the menu item appears and then the
 * call 403s. `tech` is excluded: it is an integrations role, not a support one.
 *
 * Note the asymmetry the backend enforces on top of this: a plain `admin` may
 * only act as an account with no staff role (tenants and owners), while a
 * super admin may also act as an admin/tech.
 */
export function canActAsUser(roles: AppRole[]): boolean {
  return roles.includes("admin") || roles.includes("super_admin");
}

/**
 * COL-447 — the sales-demo account may see BOTH the owner portal and the tenant
 * dashboard without signing out. True only when the account is flagged
 * (`profiles.is_demo_account`) AND is owner-portal-only.
 *
 * Presentation-only. This decides which screens the client renders; it never
 * widens what the account may read. The demo account holds the real `owner`
 * role plus real `property_owners` and booking rows, so RLS governs its queries
 * exactly as before. Keep the flag out of RLS policies, RPCs and Edge
 * Functions — the moment it grants access instead of choosing a view, it
 * becomes a way to widen reads on production.
 *
 * Gated on the role as well as the flag so that setting the column on a staff
 * or tenant account changes nothing.
 */
export function canUseDemoDualView(
  roles: AppRole[],
  isDemoAccount: boolean
): boolean {
  return isDemoAccount && isOwnerOnly(roles);
}
